/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// src/components/Risorse/MezziTerrestriTab.tsx - CON AUTOCOMPLETAMENTO TARGA
import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { SelectModal } from '../common/SelectModal';
import mezziNazionaliDB from '../../data/mezzi-nazionali.json';

// ========================================
// DATABASE MEZZI LOCALI (gestito dinamicamente)
// ========================================
const MEZZI_LOCALI_KEY = 'dos518_mezzi_locali';

const loadMezziLocali = (): Record<string, { tipo: string; grado: number }> => {
  try {
    const stored = localStorage.getItem(MEZZI_LOCALI_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveMezziLocali = (mezzi: Record<string, { tipo: string; grado: number }>) => {
  localStorage.setItem(MEZZI_LOCALI_KEY, JSON.stringify(mezzi));
};

// ========================================
// COSTANTI
// ========================================

const TIPI_MEZZO = [
  { nome: 'APS 3° Grado', gradoMinimo: 3 },
  { nome: 'Campagnola 2° Grado', gradoMinimo: 2 },
  { nome: 'Kilolitrica 4° Grado', gradoMinimo: 4 },
  { nome: 'Pickup 1° Grado', gradoMinimo: 1 },
  { nome: 'Modulo AIB 2° Grado', gradoMinimo: 2 },
];

const POSIZIONI_OPERATIVE = [
  'IN TESTA',
  'FIANCO SX',
  'FIANCO DX',
  'IN CODA',
  'BONIFICA',
  'RIENTRO',
  'MANUTENZIONE',
];

// ========================================
// FUNZIONI HELPER
// ========================================

const getGradoAutista = (qualifica: string): number => {
  if (qualifica === 'autista_1_grado') return 1;
  if (qualifica === 'autista_2_grado') return 2;
  if (qualifica === 'autista_3_grado') return 3;
  if (qualifica === 'autista_4_grado') return 4;
  return 0;
};

const getGradoMezzo = (tipoMezzo: string): number => {
  const mezzo = TIPI_MEZZO.find(m => m.nome === tipoMezzo);
  return mezzo?.gradoMinimo || 0;
};

// NUOVO: Ottieni il grado dal database usando la targa
const getGradoMezzoDaTarga = (targa: string, dbCompleto: Record<string, { tipo: string; grado: number }>): number => {
  const mezzoInfo = dbCompleto[targa];
  return mezzoInfo?.grado || 0;
};

// Unisce database nazionale e locale
const getDatabaseCompleto = (mezziLocali: Record<string, { tipo: string; grado: number }>) => {
  return { ...mezziNazionaliDB, ...mezziLocali };
};

// ========================================
// COMPONENTE PRINCIPALE
// ========================================

export const MezziTerrestriTab: React.FC = () => {
  const mezziTerrestri = useAppStore((state) => state.mezziTerrestri);
  const personale = useAppStore((state) => state.personale);
  const addMezzoTerrestre = useAppStore((state) => state.addMezzoTerrestre);
  const updateMezzoTerrestre = useAppStore((state) => state.updateMezzoTerrestre);
  const deleteMezzoTerrestre = useAppStore((state) => state.deleteMezzoTerrestre);
  const coordinate = useAppStore((state) => state.coordinate);
  const aggiornaPosizioneDoS = useAppStore((state) => state.aggiornaPosizioneDoS);

  // Posizione DOS (prima coordinata con titolo "Posizione DOS")
  const posizioneDoS = coordinate.find(
    (c) => (c as any).titolo === 'Posizione DOS' || c.name === 'Posizione DOS'
  );

  const [formData, setFormData] = useState({
    targa: '',
    tipo: '',
    ente: '',
    autista: '',
    capoSquadra: '',
    numeroComponenti: undefined as number | undefined,
    posizioneOperativa: '',
    note: '',
  });

  // Coordinate GPS acquisite per il mezzo in fase di inserimento
  const [mezzoLat, setMezzoLat] = useState<number | null>(null);
  const [mezzoLng, setMezzoLng] = useState<number | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showTipoModal, setShowTipoModal] = useState(false);
  const [showCapoModal, setShowCapoModal] = useState(false);
  const [showAutistaModal, setShowAutistaModal] = useState(false);
  const [showPosizioneModal, setShowPosizioneModal] = useState(false);
  
  // NUOVO: Stati per autocompletamento
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [targaSuggestions, setTargaSuggestions] = useState<string[]>([]);
  const [mezziLocali, setMezziLocali] = useState<Record<string, { tipo: string; grado: number }>>(loadMezziLocali());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [tipoManuale, setTipoManuale] = useState('');

  // Database completo (nazionale + locale)
  const dbCompleto = getDatabaseCompleto(mezziLocali);

  // ========================================
  // AUTOCOMPLETAMENTO TARGA
  // ========================================

  const handleTargaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().toUpperCase();
    setFormData(prev => ({ ...prev, targa: value }));

    if (value.length >= 2) {
      // Cerca targhe che iniziano con il valore inserito
      const suggestions = Object.keys(dbCompleto)
        .filter(targa => targa.startsWith(value))
        .slice(0, 10); // Massimo 10 suggerimenti
      
      setTargaSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setShowSuggestions(false);
      setTargaSuggestions([]);
    }
  };

  const handleTargaSelect = (targa: string) => {
    const mezzoInfo = dbCompleto[targa];
    
    setFormData(prev => ({
      ...prev,
      targa: targa,
      tipo: mezzoInfo.tipo
    }));
    
    setShowSuggestions(false);
    setTargaSuggestions([]);
  };

  // Verifica targa quando l'utente esce dal campo
  const handleTargaBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      
      // AUTOCOMPLETAMENTO: Se la targa esiste nel DB e il tipo non è compilato, compilalo automaticamente
      if (formData.targa && dbCompleto[formData.targa] && !formData.tipo) {
        const mezzoInfo = dbCompleto[formData.targa];
        setFormData(prev => ({
          ...prev,
          tipo: mezzoInfo.tipo
        }));
        return; // Esci subito, non mostrare il dialog
      }
      
      // MOSTRA DIALOG: Solo se la targa non esiste E il tipo non è compilato
      if (formData.targa && !dbCompleto[formData.targa] && !formData.tipo) {
        setShowConfirmDialog(true);
      }
    }, 200); // Delay per permettere il click sui suggerimenti
  };

  // Conferma aggiunta nuova targa
  const handleConfirmNuovaTarga = () => {
    if (!tipoManuale.trim()) {
      alert('⚠️ Inserisci il tipo di mezzo');
      return;
    }

    // Aggiungi al database locale
    const nuoviMezzi = {
      ...mezziLocali,
      [formData.targa]: { tipo: tipoManuale, grado: 3 } // Grado default 3
    };
    
    setMezziLocali(nuoviMezzi);
    saveMezziLocali(nuoviMezzi);
    
    setFormData(prev => ({ ...prev, tipo: tipoManuale }));
    setShowConfirmDialog(false);
    setTipoManuale('');
    
    alert(`✅ Targa ${formData.targa} aggiunta al database locale con tipo "${tipoManuale}"`);
  };

  // ========================================
  // GESTIONE FORM
  // ========================================

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let finalValue: any = value;
    if (type === 'number') {
      finalValue = value === '' ? undefined : parseInt(value);
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  // Acquisisce posizione GPS per il mezzo e aggiorna la Posizione DOS
  const handleAcquisciGPS = () => {
    if (!navigator.geolocation) {
      setGpsError('GPS non disponibile su questo dispositivo');
      return;
    }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setMezzoLat(lat);
        setMezzoLng(lng);
        setGpsLoading(false);
        // Aggiorna anche la Posizione DOS nello store
        aggiornaPosizioneDoS(lat, lng);
      },
      (err) => {
        const msgs: Record<number, string> = {
          1: 'Permesso GPS negato',
          2: 'Segnale GPS non disponibile',
          3: 'Timeout GPS — riprova',
        };
        setGpsError(msgs[err.code] ?? err.message);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Usa la Posizione DOS già salvata per il mezzo
  const handleUsaPosizioneDoS = () => {
    if (!posizioneDoS) return;
    setMezzoLat(parseFloat(String(posizioneDoS.lat)));
    setMezzoLng(parseFloat(String((posizioneDoS as any).lon ?? (posizioneDoS as any).lng)));
  };

  const handleSubmit = () => {
    if (!formData.targa || !formData.tipo) {
      alert('⚠️ Targa e Tipo Mezzo sono obbligatori');
      return;
    }

    // VALIDAZIONE GRADO AUTISTA - USA GRADO DAL DATABASE
    if (formData.autista) {
      const autista = personale.find(p => `${p.nome} ${p.cognome}`.trim() === formData.autista);
      if (autista) {
        const gradoAutista = getGradoAutista(autista.qualifica);
        const gradoMezzo = getGradoMezzoDaTarga(formData.targa, dbCompleto);
        
        if (gradoMezzo > 0 && gradoAutista < gradoMezzo) {
          alert(`❌ ERRORE: ${formData.autista} ha grado ${gradoAutista}°, ma il mezzo "${formData.tipo}" (targa ${formData.targa}) richiede grado minimo ${gradoMezzo}°!\n\nSeleziona un autista con grado adeguato.`);
          return;
        }
      }
    }

    // Risolve le coordinate: prima quelle acquisite ora, poi la Posizione DOS già salvata
    const latDaSalvare = mezzoLat ?? (posizioneDoS ? parseFloat(String(posizioneDoS.lat)) : undefined);
    const lngDaSalvare = mezzoLng ?? (posizioneDoS ? parseFloat(String((posizioneDoS as any).lon ?? (posizioneDoS as any).lng)) : undefined);

    const dataToSave = {
      sigla: formData.targa,
      tipo: formData.tipo as any,
      targa: formData.targa,
      ente: formData.ente,
      autista: formData.autista,
      capoSquadra: formData.capoSquadra,
      numeroComponenti: formData.numeroComponenti,
      posizioneOperativa: formData.posizioneOperativa as any,
      note: formData.note,
      ...(latDaSalvare !== undefined && !isNaN(latDaSalvare) && lngDaSalvare !== undefined && !isNaN(lngDaSalvare)
        ? { lat: latDaSalvare, lng: lngDaSalvare }
        : {}),
    };

    if (editingId) {
      updateMezzoTerrestre(editingId, dataToSave);
      setEditingId(null);
    } else {
      addMezzoTerrestre(dataToSave as any);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      targa: '',
      tipo: '',
      ente: '',
      autista: '',
      capoSquadra: '',
      numeroComponenti: undefined,
      posizioneOperativa: '',
      note: '',
    });
    setMezzoLat(null);
    setMezzoLng(null);
    setGpsError('');
  };

  const handleEdit = (mezzo: any) => {
    setFormData({
      targa: mezzo.targa || '',
      tipo: mezzo.tipo,
      ente: mezzo.ente || '',
      autista: mezzo.autista || '',
      capoSquadra: mezzo.capoSquadra || '',
      numeroComponenti: mezzo.numeroComponenti,
      posizioneOperativa: mezzo.posizioneOperativa,
      note: mezzo.note || '',
    });
    setEditingId(mezzo.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    if (confirm('Sei sicuro di voler eliminare questo mezzo?')) {
      deleteMezzoTerrestre(id);
    }
  };

  // ===== FILTRI PERSONALE =====
  
  const autistiAssegnati = mezziTerrestri
    .filter(m => editingId ? m.id !== editingId : true)
    .map(m => m.autista)
    .filter(Boolean);

  const capiSquadraAssegnati = mezziTerrestri
    .filter(m => editingId ? m.id !== editingId : true)
    .map(m => m.capoSquadra)
    .filter(Boolean);

  const capiSquadraDisponibili = personale
    .filter((p) => p.qualifica === 'capo_squadra')
    .filter(p => {
      const nomeCompleto = `${p.nome} ${p.cognome}`.trim();
      return !capiSquadraAssegnati.includes(nomeCompleto);
    })
    .map(p => ({
      nome: `${p.nome} ${p.cognome}`.trim(),
      qualifica: p.qualifica
    }));

  const autisti = personale
    .filter((p) => 
      p.qualifica === 'autista_1_grado' ||
      p.qualifica === 'autista_2_grado' ||
      p.qualifica === 'autista_3_grado' ||
      p.qualifica === 'autista_4_grado'
    )
    .map(p => ({
      nome: `${p.nome} ${p.cognome}`.trim(),
      qualifica: p.qualifica,
      grado: getGradoAutista(p.qualifica)
    }));

  const autistiDisponibili = autisti.filter(a => {
    // CORRETTO: Usa grado dal database invece di getGradoMezzo hardcoded
    const gradoRichiesto = formData.targa ? getGradoMezzoDaTarga(formData.targa, dbCompleto) : 0;
    const gradoOk = gradoRichiesto > 0 ? a.grado >= gradoRichiesto : true;
    const nonAssegnato = !autistiAssegnati.includes(a.nome);
    return gradoOk && nonAssegnato;
  });

  return (
    <div className="risorse-tab-content">
      <h3>🚛 Gestione Mezzi Terrestri</h3>

      <div className="form-container">
        <h4>{editingId ? '✏️ Modifica Mezzo' : '➕ Nuovo Mezzo Terrestre'}</h4>

        <div className="form-row">
          <div className="form-group" style={{ position: 'relative' }}>
            <label>Targa * 🔍</label>
            <input
              type="text"
              name="targa"
              value={formData.targa}
              onChange={handleTargaChange}
              onBlur={handleTargaBlur}
              onFocus={() => {
                if (formData.targa.length >= 2 && targaSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              placeholder="Es: 3816, 25784..."
              style={{ textTransform: 'uppercase' }}
            />
            
            {/* DROPDOWN SUGGERIMENTI */}
            {showSuggestions && targaSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'rgba(30, 30, 30, 0.98)',
                border: '2px solid #3498db',
                borderRadius: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '5px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                {targaSuggestions.map(targa => {
                  const info = dbCompleto[targa];
                  return (
                    <div
                      key={targa}
                      onClick={() => handleTargaSelect(targa)}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(52,152,219,0.2)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <span style={{ color: '#fff', fontWeight: 600 }}>{targa}</span>
                      <span style={{ color: '#3498db', fontSize: '13px' }}>
                        {info.tipo} → {info.grado}°
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Tipo Mezzo *</label>
            <input
              type="text"
              value={formData.tipo ? `${formData.tipo}${dbCompleto[formData.targa] ? ` → ${dbCompleto[formData.targa].grado}°` : ''}` : ''}
              readOnly
              placeholder="Verrà compilato automaticamente..."
              style={{ 
                background: formData.tipo ? 'rgba(46,204,113,0.1)' : 'rgba(255,255,255,0.05)',
                color: formData.tipo ? '#2ecc71' : '#999',
                cursor: 'not-allowed'
              }}
            />
            {formData.tipo && (
              <span style={{ color: '#2ecc71', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                ✅ Tipo rilevato dal database
              </span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Ente</label>
            <input
              type="text"
              name="ente"
              value={formData.ente}
              onChange={handleInputChange}
              placeholder="Es: VVFF, Forestale..."
            />
          </div>

          <div className="form-group">
            <label>Numero Componenti</label>
            <input
              type="number"
              name="numeroComponenti"
              value={formData.numeroComponenti || ''}
              onChange={handleInputChange}
              min="1"
              placeholder="Es: 5"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Autista</label>
            <input
              type="text"
              value={formData.autista || ''}
              readOnly
              onClick={() => formData.tipo && setShowAutistaModal(true)}
              placeholder={formData.tipo ? 'Seleziona Autista...' : 'Prima seleziona tipo mezzo'}
              style={{ 
                cursor: formData.tipo ? 'pointer' : 'not-allowed',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                opacity: formData.tipo ? 1 : 0.5
              }}
            />
          </div>

          <div className="form-group">
            <label>Capo Squadra</label>
            <input
              type="text"
              value={formData.capoSquadra || ''}
              readOnly
              onClick={() => setShowCapoModal(true)}
              placeholder="Seleziona Capo Squadra..."
              style={{ 
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Posizione Operativa</label>
            <input
              type="text"
              value={formData.posizioneOperativa || ''}
              readOnly
              onClick={() => setShowPosizioneModal(true)}
              placeholder="Seleziona Posizione..."
              style={{ 
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Note</label>
          <textarea
            name="note"
            value={formData.note}
            onChange={handleInputChange}
            rows={3}
            placeholder="Note aggiuntive..."
          />
        </div>

        {/* ── Sezione Coordinate GPS ── */}
        <div style={{
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
        }}>
          <div style={{ fontWeight: 700, color: '#93c5fd', fontSize: '0.82rem', marginBottom: 8, letterSpacing: 0.5 }}>
            📍 POSIZIONE SULLA MAPPA SI.TA.C.
          </div>

          {/* Stato coordinate */}
          {mezzoLat !== null ? (
            <div style={{ fontSize: '0.78rem', color: '#6ee7b7', marginBottom: 8, fontWeight: 600 }}>
              ✅ GPS acquisito: {mezzoLat.toFixed(5)}, {mezzoLng!.toFixed(5)}
            </div>
          ) : posizioneDoS ? (
            <div style={{ fontSize: '0.78rem', color: '#fcd34d', marginBottom: 8 }}>
              📌 Userà Posizione DOS: {parseFloat(String(posizioneDoS.lat)).toFixed(5)}, {parseFloat(String((posizioneDoS as any).lon)).toFixed(5)}
            </div>
          ) : (
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 8 }}>
              Nessuna coordinata. Acquisisci GPS o posiziona manualmente sulla mappa.
            </div>
          )}

          {gpsError && (
            <div style={{ fontSize: '0.75rem', color: '#f87171', marginBottom: 8 }}>{gpsError}</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleAcquisciGPS}
              disabled={gpsLoading}
              style={{
                background: gpsLoading ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.2)',
                border: '1px solid rgba(59,130,246,0.5)',
                color: '#93c5fd',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: gpsLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {gpsLoading ? '⌛ Acquisizione...' : '📡 Rileva posizione GPS'}
            </button>

            {posizioneDoS && mezzoLat === null && (
              <button
                type="button"
                onClick={handleUsaPosizioneDoS}
                style={{
                  background: 'rgba(251,191,36,0.12)',
                  border: '1px solid rgba(251,191,36,0.4)',
                  color: '#fcd34d',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                📌 Usa Posizione DOS
              </button>
            )}

            {mezzoLat !== null && (
              <button
                type="button"
                onClick={() => { setMezzoLat(null); setMezzoLng(null); }}
                style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.35)',
                  color: '#fca5a5',
                  borderRadius: 6,
                  padding: '6px 10px',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button 
            className="btn-submit" 
            onClick={handleSubmit}
            style={{
              background: editingId ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' : 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)';
            }}
          >
            {editingId ? '✅ Aggiorna Mezzo' : '➕ Aggiungi Mezzo'}
          </button>
          {editingId && (
            <button 
              className="btn-cancel" 
              onClick={() => { setEditingId(null); resetForm(); }}
              style={{
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)';
              }}
            >
              ❌ Annulla Modifica
            </button>
          )}
        </div>
      </div>

      {/* LISTA MEZZI */}
      <div className="lista-header">
        <h4>Lista Mezzi Terrestri ({mezziTerrestri.length})</h4>
      </div>

      {/* ── Card Posizione DOS ── */}
      <div style={{
        background: posizioneDoS
          ? 'linear-gradient(135deg,rgba(251,191,36,0.12),rgba(217,119,6,0.08))'
          : 'rgba(255,255,255,0.03)',
        border: posizioneDoS ? '1.5px solid rgba(251,191,36,0.45)' : '1.5px dashed rgba(255,255,255,0.15)',
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <div style={{
          width: 42, height: 42, flexShrink: 0,
          background: posizioneDoS ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'rgba(255,255,255,0.07)',
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
        }}>🎖️</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: posizioneDoS ? '#fcd34d' : '#94a3b8' }}>
            Posizione DOS
          </div>
          {posizioneDoS ? (
            <div style={{ fontSize: '0.74rem', color: '#fbbf24', marginTop: 2 }}>
              {parseFloat(String(posizioneDoS.lat)).toFixed(5)},&nbsp;
              {parseFloat(String((posizioneDoS as any).lon)).toFixed(5)}
              {(posizioneDoS as any).timestamp && (
                <span style={{ color: '#94a3b8', marginLeft: 6 }}>
                  · {(posizioneDoS as any).timestamp}
                </span>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
              Non ancora rilevata
            </div>
          )}
        </div>
        <button
          onClick={handleAcquisciGPS}
          disabled={gpsLoading}
          style={{
            flexShrink: 0,
            background: gpsLoading ? 'rgba(251,191,36,0.15)' : 'rgba(251,191,36,0.2)',
            border: '1px solid rgba(251,191,36,0.5)',
            color: gpsLoading ? '#94a3b8' : '#fcd34d',
            borderRadius: 6, padding: '6px 11px',
            fontSize: '0.78rem', fontWeight: 700,
            cursor: gpsLoading ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {gpsLoading ? '⌛' : posizioneDoS ? '📡 Aggiorna' : '📡 Rileva GPS'}
        </button>
      </div>

      <div className="risorse-list">
        {mezziTerrestri.length === 0 ? (
          <div className="empty-state">
            <p>🚛 Nessun mezzo inserito</p>
          </div>
        ) : (
          mezziTerrestri.map((mezzo) => (
            <div key={mezzo.id} className="risorsa-card">
              <div className="card-header-risorsa">
                <span className="icon">🚛</span>
                <h4>
                  {mezzo.targa || mezzo.sigla}
                  <span className={`badge status-${mezzo.posizioneOperativa?.toLowerCase().replace(/\s/g, '_')}`}>
                    {mezzo.posizioneOperativa}
                  </span>
                </h4>
                {mezzo.lat !== undefined && (
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '0.7rem', fontWeight: 700,
                    background: 'rgba(34,197,94,0.15)',
                    border: '1px solid rgba(34,197,94,0.35)',
                    color: '#86efac',
                    borderRadius: 4, padding: '2px 6px',
                    whiteSpace: 'nowrap',
                  }}>
                    📍 Su mappa
                  </span>
                )}
              </div>
              <div className="card-body-risorsa">
                <p><strong>Tipo:</strong> {mezzo.tipo}</p>
                <p><strong>Ente:</strong> {mezzo.ente || 'N/D'}</p>
                <p><strong>Autista:</strong> {mezzo.autista || 'N/D'}</p>
                <p><strong>Capo Squadra:</strong> {mezzo.capoSquadra || 'N/D'}</p>
                {mezzo.numeroComponenti && <p><strong>Componenti:</strong> {mezzo.numeroComponenti}</p>}
                {mezzo.lat !== undefined && (
                  <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                    📍 {Number(mezzo.lat).toFixed(5)}, {Number(mezzo.lng).toFixed(5)}
                  </p>
                )}
              </div>
              <div className="card-actions">
                <button className="btn-edit" onClick={() => handleEdit(mezzo)}>✏️ Modifica</button>
                <button className="btn-delete" onClick={() => handleDelete(mezzo.id)}>🗑️ Elimina</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* MODALE CONFERMA TARGA NON TROVATA */}
      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ TARGA NON TROVATA</h3>
              <button className="btn-close" onClick={() => setShowConfirmDialog(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '20px', color: '#f39c12' }}>
                La targa <strong>{formData.targa}</strong> non è presente nel database nazionale.
              </p>
              <p style={{ marginBottom: '15px' }}>Inserisci manualmente il tipo di mezzo per aggiungerla al database locale:</p>
              
              <input
                type="text"
                value={tipoManuale}
                onChange={(e) => setTipoManuale(e.target.value.toUpperCase())}
                placeholder="Es: ABP, AV, CA/PU..."
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '15px',
                  marginBottom: '20px',
                  textTransform: 'uppercase'
                }}
              />

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleConfirmNuovaTarga}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#2ecc71',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  ✅ Conferma e Aggiungi
                </button>
                <button
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setTipoManuale('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#e74c3c',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  ❌ Annulla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALI ESISTENTI (Autista, Capo, Posizione) */}
      {showCapoModal && (
        <div className="modal-overlay" onClick={() => setShowCapoModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👨‍🚒 SELEZIONA CAPO SQUADRA</h3>
              <button className="btn-close" onClick={() => setShowCapoModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {capiSquadraAssegnati.length > 0 && (
                <div style={{marginBottom:'15px',padding:'10px',background:'rgba(231,76,60,0.1)',border:'1px solid #e74c3c',borderRadius:'6px'}}>
                  <p style={{color:'#e74c3c',fontSize:'12px',margin:0}}>
                    <strong>Già assegnati:</strong> {capiSquadraAssegnati.join(', ')}
                  </p>
                </div>
              )}
              
              {capiSquadraDisponibili.length === 0 ? (
                <p style={{color:'#e74c3c',textAlign:'center',padding:'20px'}}>
                  ❌ Nessun capo squadra disponibile
                </p>
              ) : (
                capiSquadraDisponibili.map(capo => (
                  <button
                    key={capo.nome}
                    onClick={() => {
                      setFormData({ ...formData, capoSquadra: capo.nome });
                      setShowCapoModal(false);
                    }}
                    style={{
                      width:'100%',
                      padding:'15px',
                      marginBottom:'10px',
                      background: formData.capoSquadra === capo.nome ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.1)',
                      border: `2px solid ${formData.capoSquadra === capo.nome ? '#2ecc71' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius:'8px',
                      color:'#fff',
                      fontSize:'15px',
                      cursor:'pointer',
                      textAlign:'left'
                    }}
                  >
                    {capo.nome}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showAutistaModal && (
        <div className="modal-overlay" onClick={() => setShowAutistaModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👤 SELEZIONA AUTISTA</h3>
              <button className="btn-close" onClick={() => setShowAutistaModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {formData.targa && dbCompleto[formData.targa] && (
                <div style={{marginBottom:'15px',padding:'10px',background:'rgba(52,152,219,0.1)',border:'1px solid #3498db',borderRadius:'6px'}}>
                  <p style={{color:'#3498db',fontSize:'13px',margin:0}}>
                    <strong>{formData.tipo}</strong> (Targa {formData.targa}) richiede grado minimo <strong>{dbCompleto[formData.targa].grado}°</strong>
                  </p>
                </div>
              )}

              {autistiAssegnati.length > 0 && (
                <div style={{marginBottom:'15px',padding:'10px',background:'rgba(231,76,60,0.1)',border:'1px solid #e74c3c',borderRadius:'6px'}}>
                  <p style={{color:'#e74c3c',fontSize:'12px',margin:0}}>
                    <strong>Già assegnati:</strong> {autistiAssegnati.join(', ')}
                  </p>
                </div>
              )}
              
              {autistiDisponibili.length === 0 ? (
                <p style={{color:'#e74c3c',textAlign:'center',padding:'20px'}}>
                  ❌ Nessun autista disponibile con grado {formData.targa && dbCompleto[formData.targa] ? dbCompleto[formData.targa].grado+'°' : ''} o superiore
                </p>
              ) : (
                autistiDisponibili.map(autista => (
                  <button
                    key={autista.nome}
                    onClick={() => {
                      setFormData({ ...formData, autista: autista.nome });
                      setShowAutistaModal(false);
                    }}
                    style={{
                      width:'100%',
                      padding:'15px',
                      marginBottom:'10px',
                      background: formData.autista === autista.nome ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.1)',
                      border: `2px solid ${formData.autista === autista.nome ? '#2ecc71' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius:'8px',
                      color:'#fff',
                      fontSize:'15px',
                      cursor:'pointer',
                      textAlign:'left',
                      display:'flex',
                      justifyContent:'space-between',
                      alignItems:'center'
                    }}
                  >
                    <span>{autista.nome}</span>
                    <span style={{
                      fontSize:'12px',
                      color: '#2ecc71',
                      fontWeight:'600'
                    }}>
                      {autista.grado}° grado ✅
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <SelectModal
        isOpen={showPosizioneModal}
        onClose={() => setShowPosizioneModal(false)}
        title="Seleziona Posizione Operativa"
        options={POSIZIONI_OPERATIVE}
        selectedValue={formData.posizioneOperativa}
        onSelect={(value) => setFormData({ ...formData, posizioneOperativa: value })}
      />
    </div>
  );
};
